import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "react-bootstrap/Alert";
import {Camera} from "react-html5-camera-photo";
import "react-html5-camera-photo/build/css/index.css";
import capture from "../../../assets/photo_capture.jpeg";
import {Spinner} from "react-bootstrap";
import apiClient from "../../../utils/axios";

function UploadButtonPersonal({appData, onFileUpload}) {

    const {t} = useTranslation();
    const [showAlert, setShowAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successAlert, setSuccessAlert] = useState(false);
    const fileInputRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const savedFiles = JSON.parse(sessionStorage.getItem("uploadedFiles"));
        if (savedFiles) {
            setSelectedFiles(savedFiles);
            onFileUpload(true);
        }
    }, [onFileUpload]);

    useEffect(() => {
        if (showAlert) {
            const timer = setTimeout(() => setShowAlert(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [showAlert]);

    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (successAlert) {
            const timer = setTimeout(() => setSuccessAlert(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [successAlert]);

    const handleUpload = async (event) => {
        event?.preventDefault?.();
        const files = event.target?.files || event.target?.selectedFiles || [];
        if (!files.length) return;

        const newFileNames = [];
        for (const file of files) {
            if (file.size > 6 * 1024 * 1024) {
                setErrorMessage('File size too large. Maximum size is 6MB.');
                setShowAlert(true);
                return;
            }

            const allowedTypes = ["pdf", "jpeg", "gif", "png", "jpg"];
            const fileType = file.type.split("/")[1];
            if (!allowedTypes.includes(fileType)) {
                setErrorMessage("File type not allowed. Allowed types: pdf, jpeg, gif, png, jpg");
                setShowAlert(true);
                return;
            }

            const formData = new FormData();
            formData.append("file", file);

            try {
                const response = await apiClient.post(
                    `${process.env.REACT_APP_API_URL}/api/accounts/upload/${appData.app_id}`,
                    formData,
                    {headers: {"Content-Type": "multipart/form-data"}}
                );

                if (response.data.status === '403') {
                    setErrorMessage('Upload failed. Please try again.');
                    setShowAlert(true);
                } else {
                    newFileNames.push(file.name);
                }

                if (response.data && response.data.status &&
                    ['400', '401', '403', '404', '405', '408', '429', '500', '501', '502', '503', '504', '505'].includes(response.data.status)) {
                    throw new Error(response.data.message || 'Unknown error occurred');
                }

            } catch (error) {
                console.error("Unhandled error:", error);
                setErrorMessage('File upload failed.');
                setShowAlert(true);
                onFileUpload(false);
                return;
            }
        }

        const updatedFiles = [...selectedFiles, ...newFileNames];
        setSelectedFiles(updatedFiles);
        sessionStorage.setItem("uploadedFiles", JSON.stringify(updatedFiles));
        setSuccessAlert(true);
        onFileUpload(true);
        fileInputRef.current.value = null;
    };

    const onTakePhoto = (dataUri) => {
        setPreviewImage(dataUri);
        setIsPreviewOpen(true);
        setIsPopupOpen(false);
    };

    const handleContinue = async () => {
        setIsLoading(true);
        try {
            const base64Image = previewImage.split(",")[1];
            const blob = base64toBlob(base64Image, "image/png");
            const fileName = "identification.png";
            const selectedFile = new File([blob], fileName, {type: "image/png"});

            await handleUpload({target: {selectedFiles: [selectedFile]}});
            setPreviewImage(null);
            setIsPreviewOpen(false);
        } catch (error) {
            console.error("Error uploading photo:", error);
            setErrorMessage("Error uploading photo. Please try again.");
            setShowAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetake = () => {
        setPreviewImage(null);
        setIsPreviewOpen(false);
        setIsPopupOpen(true);
    };

    const base64toBlob = (base64Data, contentType) => {
        const byteCharacters = atob(base64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
            const slice = byteCharacters.slice(offset, offset + 1024);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, {type: contentType});
    };

    const openPopup = (event) => {
        event.preventDefault();
        setIsPopupOpen(true);
    };

    const closePopup = () => setIsPopupOpen(false);
    const closePreviewPopup = () => setIsPreviewOpen(false);

    const isMobile = () => /Mobi|Android/i.test(navigator.userAgent);

    return (
        <div className="w-full mt-3">
            <div
                className="upload_capture_personal_btns flex justify-content-center align-items-center sm:flex-row flex-column flex-wrap sm:gap-3 gap-1">
                <label
                    className="upload_btn w-auto border-0 text-white text-center cursor-pointer font-normal text-3xl px-4 py-2">
                    <input
                        type="file"
                        className="hidden"
                        onChange={handleUpload}
                        ref={fileInputRef}
                        multiple
                    />
                    <span>{t("welcome_to.upload")}</span>
                </label>

                <h6 className="mb-0">{t("errors.or")}</h6>

                <button
                    className="take_photo_btn w-auto border-0 px-3 py-2 text-white text-center cursor-pointer font-normal text-3xl"
                    onClick={openPopup}>
                    {t("welcome_to.capture_image")}
                </button>
            </div>

            {isPopupOpen && (
                <div
                    className="popup fixed top-0 left-0 w-full h-full flex justify-content-center align-items-center z-5">
                    <div className="popup-content bg-white p-3 relative">
                        <span className="close absolute cursor-pointer" onClick={closePopup}>&times;</span>
                        <div
                            className="flex sm:flex-row flex-column sm:align-items-end align-items-center justify-content-center pb-2">
                            <img width="120" height="120" src={capture} alt=""/>
                            <div className="capture_text text-center">
                                <h3>{t("welcome_to.picture_id")}</h3>
                                <p className="mb-3">{t("welcome_to.confirm_doc")}</p>
                            </div>
                        </div>

                        <Camera
                            onTakePhoto={onTakePhoto}
                            isFullscreen={false}
                            idealFacingMode={isMobile() ? "environment" : "user"}
                        />
                    </div>
                </div>
            )}

            {isPreviewOpen && (
                <div
                    className="preview-popup fixed top-0 left-0 w-full h-full flex justify-content-center align-items-center z-5">
                    <div className="preview-content bg-white p-3 relative text-center">
                        <div className="flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">{t("welcome_to.photo_text")}</h5>
                            <span className="close absolute cursor-pointer" onClick={closePreviewPopup}>&times;</span>
                        </div>
                        <img src={previewImage} alt="Captured"
                             className="preview-image w-full h-auto max-w-25rem mb-3"/>
                        <div className="flex justify-content-around flex-wrap gap-2">
                            <button
                                onClick={handleContinue}
                                className="preview-button border-0 border-round-sm cursor-pointer text-white font-base px-2 py-3"
                                disabled={isLoading}
                            >
                                {isLoading ? <Spinner animation="border" size="sm"/> : t("next_btn")}
                            </button>
                            <button onClick={handleRetake}
                                    className="preview-button border-0 border-round-sm cursor-pointer text-white font-base px-2 py-3">
                                {t("welcome_to.retake_photo")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="selected-files-personal">
                {selectedFiles.length > 0 && (
                    <ul className="p-0" style={{width: "fit-content"}}>
                        {selectedFiles.map((file, index) => (
                            <li key={index} className="file_list_personal w-max list-none">{file}</li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="alert_message_personal m-0 text-center ">
                {showAlert && (
                    <Alert variant="danger">
                        {errorMessage || "Only allowed pdf, jpeg, gif, png, jpg files with a size under 6MB."}
                    </Alert>
                )}

                {successAlert && !errorMessage && !showAlert && (
                    <Alert variant="success">
                        File uploaded successfully
                    </Alert>
                )}
            </div>
        </div>
    );
}

export default UploadButtonPersonal;